import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Sidebar } from '@/components/common/Sidebar';
import {
  getDefaultRouteByRole,
  isAdminRole,
  shouldFetchAdminResources,
} from '@/lib/roleRouting';
import { ThemeToggle } from '@/components/common';
import {
  TrendingUp,
  Users,
  Calendar,
  DollarSign,
  AlertTriangle,
  Stethoscope,
  Flag,
  Settings,
  Loader2,
  AlertCircle,
  FileText,
  Search,
  Filter,
  RefreshCw,
  Award,
  Clock,
  ShieldCheck,
  TrendingDown,
  ChevronRight,
  Info
} from 'lucide-react';
import {
  getAnalyticsOverview,
  getAnalyticsAppointments,
  getAnalyticsFinancial,
  getAnalyticsDoctors,
  getAnalyticsReports,
  getAnalyticsOperations,
  AnalyticsFilter,
  AnalyticsOverview,
  AnalyticsAppointments,
  AnalyticsFinancial,
  AnalyticsDoctors,
  AnalyticsReports,
  AnalyticsOperations
} from '@/services/admins';
import { listDoctors } from '@/services/doctors';
import type { DoctorDTO } from '@/services/doctors';
import { toast } from 'sonner';
import { GlobalSearch, NotificationCenter } from '@/components/dashboard/HeaderActions';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';

type DatePreset = 'today' | '7days' | '30days' | 'current_month' | 'last_month' | 'current_year' | 'custom';
type TabType = 'overview' | 'appointments' | 'financial' | 'doctors' | 'reports' | 'operations';

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { user, isLoading, logout } = useAuth();
  
  // Tabs and loading
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters State
  const [preset, setPreset] = useState<DatePreset>('30days');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('all');
  const [selectedDoctorId, setSelectedDoctorId] = useState<string>('all');
  const [cityFilter, setCityFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');

  // Doctor List (for dropdown)
  const [allDoctors, setAllDoctors] = useState<DoctorDTO[]>([]);

  // Analytics Data States
  const [overviewData, setOverviewData] = useState<AnalyticsOverview | null>(null);
  const [appointmentsData, setAppointmentsData] = useState<AnalyticsAppointments | null>(null);
  const [financialData, setFinancialData] = useState<AnalyticsFinancial | null>(null);
  const [doctorsData, setDoctorsData] = useState<AnalyticsDoctors | null>(null);
  const [reportsData, setReportsData] = useState<AnalyticsReports | null>(null);
  const [operationsData, setOperationsData] = useState<AnalyticsOperations | null>(null);

  // Check auth
  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        navigate('/login');
        return;
      }
      if (!isAdminRole(user.role)) {
        navigate(getDefaultRouteByRole(user.role), { replace: true });
      }
    }
  }, [user, isLoading, navigate]);

  // Load doctors for filter
  useEffect(() => {
    async function loadFilterDoctors() {
      try {
        const response = await listDoctors();
        if (response.success && Array.isArray(response.data)) {
          setAllDoctors(response.data);
        }
      } catch (err) {
        console.error('Erro ao carregar médicos para filtros:', err);
      }
    }
    if (shouldFetchAdminResources(user?.role, isLoading)) {
      loadFilterDoctors();
    }
  }, [user, isLoading]);

  // Helper to map presets to date strings
  const getFilterDates = (presetValue: DatePreset): { startDate?: string; endDate?: string } => {
    const now = new Date();
    const endISO = now.toISOString();

    switch (presetValue) {
      case 'today': {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        return { startDate: start.toISOString(), endDate: endISO };
      }
      case '7days': {
        const start = new Date();
        start.setDate(now.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        return { startDate: start.toISOString(), endDate: endISO };
      }
      case '30days': {
        const start = new Date();
        start.setDate(now.getDate() - 30);
        start.setHours(0, 0, 0, 0);
        return { startDate: start.toISOString(), endDate: endISO };
      }
      case 'current_month': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        return { startDate: start.toISOString(), endDate: endISO };
      }
      case 'last_month': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        return { startDate: start.toISOString(), endDate: end.toISOString() };
      }
      case 'current_year': {
        const start = new Date(now.getFullYear(), 0, 1);
        return { startDate: start.toISOString(), endDate: endISO };
      }
      case 'custom': {
        const startDate = customStart ? new Date(customStart).toISOString() : undefined;
        const endDate = customEnd ? new Date(customEnd).toISOString() : undefined;
        return { startDate, endDate };
      }
      default:
        return {};
    }
  };

  // Build filters object
  const buildFilters = (): AnalyticsFilter => {
    const { startDate, endDate } = getFilterDates(preset);
    
    return {
      startDate,
      endDate,
      type: selectedType !== 'all' ? (selectedType as any) : undefined,
      status: selectedStatus !== 'all' ? selectedStatus : undefined,
      specialty: selectedSpecialty !== 'all' ? selectedSpecialty : undefined,
      doctorId: selectedDoctorId !== 'all' ? selectedDoctorId : undefined,
      city: cityFilter.trim() !== '' ? cityFilter : undefined,
      state: stateFilter.trim() !== '' ? stateFilter : undefined,
    };
  };

  // Fetch current tab data
  const fetchData = async () => {
    if (!shouldFetchAdminResources(user?.role, isLoading)) return;

    setLoading(true);
    setError(null);
    const filters = buildFilters();

    if (import.meta.env.DEV) {
      console.log(`[Analytics] Buscando dados para aba: ${activeTab} com filtros:`, filters);
    }

    try {
      if (activeTab === 'overview') {
        const res = await getAnalyticsOverview(filters);
        if (import.meta.env.DEV) console.log('[Analytics] Resposta overview:', res);
        if (res.success && res.data) setOverviewData(res.data);
        else throw new Error(res.error || 'Erro ao carregar visão geral');
      } else if (activeTab === 'appointments') {
        const res = await getAnalyticsAppointments(filters);
        if (import.meta.env.DEV) console.log('[Analytics] Resposta appointments:', res);
        if (res.success && res.data) setAppointmentsData(res.data);
        else throw new Error(res.error || 'Erro ao carregar consultas');
      } else if (activeTab === 'financial') {
        const res = await getAnalyticsFinancial(filters);
        if (import.meta.env.DEV) console.log('[Analytics] Resposta financial:', res);
        if (res.success && res.data) setFinancialData(res.data);
        else throw new Error(res.error || 'Erro ao carregar dados financeiros');
      } else if (activeTab === 'doctors') {
        const res = await getAnalyticsDoctors(filters);
        if (import.meta.env.DEV) console.log('[Analytics] Resposta doctors:', res);
        if (res.success && res.data) setDoctorsData(res.data);
        else throw new Error(res.error || 'Erro ao carregar performance de médicos');
      } else if (activeTab === 'reports') {
        const res = await getAnalyticsReports(filters);
        if (import.meta.env.DEV) console.log('[Analytics] Resposta reports:', res);
        if (res.success && res.data) setReportsData(res.data);
        else throw new Error(res.error || 'Erro ao carregar denúncias');
      } else if (activeTab === 'operations') {
        const res = await getAnalyticsOperations(filters);
        if (import.meta.env.DEV) console.log('[Analytics] Resposta operations:', res);
        if (res.success && res.data) setOperationsData(res.data);
        else throw new Error(res.error || 'Erro ao carregar operações');
      }
    } catch (err: any) {
      console.error(`[Analytics] Erro ao buscar dados (${activeTab}):`, err);
      setError(err.message || 'Erro ao comunicar com o servidor analítico.');
    } finally {
      setLoading(false);
    }
  };

  // Trigger fetch on tab, preset, or select change
  useEffect(() => {
    fetchData();
  }, [activeTab, preset, selectedType, selectedStatus, selectedSpecialty, selectedDoctorId]);

  const handleApplyCustomDates = () => {
    if (preset === 'custom') {
      fetchData();
    }
  };

  const handleClearFilters = () => {
    setPreset('30days');
    setSelectedType('all');
    setSelectedStatus('all');
    setSelectedSpecialty('all');
    setSelectedDoctorId('all');
    setCityFilter('');
    setStateFilter('');
    toast.success('Filtros restaurados!');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <Loader2 className="w-12 h-12 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (!isAdminRole(user.role)) {
    return null;
  }

  // Curated color charts palette
  const COLORS = {
    teal: ['#0d9488', '#0f766e', '#115e59', '#134e4a', '#2dd4bf'],
    blue: ['#2563eb', '#1d4ed8', '#1e40af', '#1e3a8a', '#60a5fa'],
    amber: ['#d97706', '#b45309', '#92400e', '#78350f', '#fbbf24'],
    rose: ['#e11d48', '#be123c', '#9f1239', '#881337', '#fda4af'],
    pie: ['#0d9488', '#2563eb', '#fbbf24', '#e11d48', '#8b5cf6', '#6b7280'],
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex text-gray-900 dark:text-gray-100">
      {/* Sidebar */}
      <Sidebar userRole={user.role} userName={user.name} onLogout={handleLogout} />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-8 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Análises e Relatórios</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Indicadores corporativos consolidados da saúde do negócio e da operação
            </p>
          </div>

          <div className="flex items-center gap-4">
            <GlobalSearch />
            <NotificationCenter />
            <ThemeToggle />
          </div>
        </header>

        {/* Content Container */}
        <main className="flex-1 p-8 space-y-6 overflow-y-auto">
          
          {/* Filters Card */}
          <section className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-bold text-sm text-teal-600 dark:text-teal-400 uppercase tracking-wider">
                <Filter className="w-4 h-4" />
                Filtros e Parâmetros
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchData}
                  disabled={loading}
                  className="p-1.5 rounded-lg border border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors text-xs font-semibold flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                  Atualizar
                </button>
                <button
                  onClick={handleClearFilters}
                  className="p-1.5 rounded-lg border border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors text-xs font-semibold text-gray-500 cursor-pointer"
                >
                  Limpar
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {/* Preset */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500">Período de Análise</label>
                <select
                  value={preset}
                  onChange={(e) => setPreset(e.target.value as DatePreset)}
                  className="p-2 text-sm rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent dark:bg-neutral-800 focus:ring-1 focus:ring-teal-500 outline-none"
                >
                  <option value="today">Hoje</option>
                  <option value="7days">Últimos 7 dias</option>
                  <option value="30days">Últimos 30 dias</option>
                  <option value="current_month">Mês Atual</option>
                  <option value="last_month">Mês Anterior</option>
                  <option value="current_year">Ano Atual</option>
                  <option value="custom">Período Personalizado</option>
                </select>
              </div>

              {/* Specialty */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500">Especialidade Médica</label>
                <select
                  value={selectedSpecialty}
                  onChange={(e) => setSelectedSpecialty(e.target.value)}
                  className="p-2 text-sm rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent dark:bg-neutral-800 focus:ring-1 focus:ring-teal-500 outline-none"
                >
                  <option value="all">Todas as Especialidades</option>
                  <option value="Clínica Geral">Clínica Geral</option>
                  <option value="Cardiologia">Cardiologia</option>
                  <option value="Pediatria">Pediatria</option>
                  <option value="Ginecologia">Ginecologia</option>
                  <option value="Dermatologia">Dermatologia</option>
                  <option value="Ortopedia">Ortopedia</option>
                </select>
              </div>

              {/* Consultation Type */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500">Tipo de Atendimento</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="p-2 text-sm rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent dark:bg-neutral-800 focus:ring-1 focus:ring-teal-500 outline-none"
                >
                  <option value="all">Todos os Tipos</option>
                  <option value="video">Telemedicina</option>
                  <option value="home">Domiciliar</option>
                  <option value="clinic">Consultório</option>
                </select>
              </div>

              {/* Status */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500">Status do Atendimento</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="p-2 text-sm rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent dark:bg-neutral-800 focus:ring-1 focus:ring-teal-500 outline-none"
                >
                  <option value="all">Todos os Status</option>
                  <option value="completed">Concluído</option>
                  <option value="scheduled">Agendado</option>
                  <option value="requested">Pendente</option>
                  <option value="cancelled">Cancelado</option>
                </select>
              </div>

              {/* Doctor selection */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500">Médico Específico</label>
                <select
                  value={selectedDoctorId}
                  onChange={(e) => setSelectedDoctorId(e.target.value)}
                  className="p-2 text-sm rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent dark:bg-neutral-800 focus:ring-1 focus:ring-teal-500 outline-none"
                >
                  <option value="all">Todos os Médicos</option>
                  {allDoctors.map((doc) => (
                    <option key={doc.id} value={doc.userId}>
                      {doc.user?.name || doc.crm}
                    </option>
                  ))}
                </select>
              </div>

              {/* Region City */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500">Cidade (Região)</label>
                <input
                  type="text"
                  placeholder="Ex: São Paulo"
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  onBlur={fetchData}
                  className="p-2 text-sm rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent dark:bg-neutral-800 focus:ring-1 focus:ring-teal-500 outline-none"
                />
              </div>

              {/* Region State */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500">Estado (UF)</label>
                <input
                  type="text"
                  placeholder="Ex: SP"
                  maxLength={2}
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  onBlur={fetchData}
                  className="p-2 text-sm rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent dark:bg-neutral-800 focus:ring-1 focus:ring-teal-500 outline-none"
                />
              </div>
            </div>

            {/* Custom Dates Inputs */}
            {preset === 'custom' && (
              <div className="flex items-center gap-3 pt-2 animate-in fade-in duration-200">
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-gray-400 uppercase">Início</span>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="p-2 text-xs rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent dark:bg-neutral-800 outline-none"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-gray-400 uppercase">Fim</span>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="p-2 text-xs rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent dark:bg-neutral-800 outline-none"
                  />
                </div>
                <button
                  onClick={handleApplyCustomDates}
                  className="mt-5 px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-bold transition-all shadow-xs cursor-pointer"
                >
                  Aplicar Período
                </button>
              </div>
            )}
          </section>

          {/* Navigation Tabs */}
          <div className="border-b border-gray-200 dark:border-neutral-800 flex gap-2 overflow-x-auto pb-px">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex items-center gap-2 px-5 py-3 font-semibold text-sm transition-all border-b-2 whitespace-nowrap cursor-pointer ${
                activeTab === 'overview'
                  ? 'text-teal-600 dark:text-teal-400 border-teal-600 font-bold'
                  : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-800 dark:hover:text-white'
              }`}
            >
              <TrendingUp className="w-4 h-4" />
              Crescimento
            </button>
            <button
              onClick={() => setActiveTab('appointments')}
              className={`flex items-center gap-2 px-5 py-3 font-semibold text-sm transition-all border-b-2 whitespace-nowrap cursor-pointer ${
                activeTab === 'appointments'
                  ? 'text-teal-600 dark:text-teal-400 border-teal-600 font-bold'
                  : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-800 dark:hover:text-white'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Consultas
            </button>
            <button
              onClick={() => setActiveTab('financial')}
              className={`flex items-center gap-2 px-5 py-3 font-semibold text-sm transition-all border-b-2 whitespace-nowrap cursor-pointer ${
                activeTab === 'financial'
                  ? 'text-teal-600 dark:text-teal-400 border-teal-600 font-bold'
                  : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-800 dark:hover:text-white'
              }`}
            >
              <DollarSign className="w-4 h-4" />
              Faturamento
            </button>
            <button
              onClick={() => setActiveTab('doctors')}
              className={`flex items-center gap-2 px-5 py-3 font-semibold text-sm transition-all border-b-2 whitespace-nowrap cursor-pointer ${
                activeTab === 'doctors'
                  ? 'text-teal-600 dark:text-teal-400 border-teal-600 font-bold'
                  : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-800 dark:hover:text-white'
              }`}
            >
              <Stethoscope className="w-4 h-4" />
              Médicos (Qualidade)
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`flex items-center gap-2 px-5 py-3 font-semibold text-sm transition-all border-b-2 whitespace-nowrap cursor-pointer ${
                activeTab === 'reports'
                  ? 'text-teal-600 dark:text-teal-400 border-teal-600 font-bold'
                  : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-800 dark:hover:text-white'
              }`}
            >
              <Flag className="w-4 h-4" />
              Denúncias
            </button>
            <button
              onClick={() => setActiveTab('operations')}
              className={`flex items-center gap-2 px-5 py-3 font-semibold text-sm transition-all border-b-2 whitespace-nowrap cursor-pointer ${
                activeTab === 'operations'
                  ? 'text-teal-600 dark:text-teal-400 border-teal-600 font-bold'
                  : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-800 dark:hover:text-white'
              }`}
            >
              <Settings className="w-4 h-4" />
              Operacional
            </button>
          </div>

          {/* Loading Indicator inside panel */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-teal-600 animate-spin mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400 font-semibold animate-pulse">
                Processando dados consolidados...
              </p>
            </div>
          )}

          {/* Error Alert Panel */}
          {error && !loading && (
            <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/50 p-6 rounded-2xl flex flex-col items-center justify-center text-center">
              <AlertCircle className="w-12 h-12 text-red-600 mb-3" />
              <h4 className="font-bold text-red-800 dark:text-red-300">Falha ao Buscar Dados</h4>
              <p className="text-xs text-red-600 dark:text-red-400 max-w-sm mt-1">{error}</p>
              <button
                onClick={fetchData}
                className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition-all shadow-xs cursor-pointer"
              >
                Tentar Novamente
              </button>
            </div>
          )}

          {/* ───────────────────────────────────────────────────────────── */}
          {/* TAB: PLATFORM GROWTH OVERVIEW */}
          {/* ───────────────────────────────────────────────────────────── */}
          {!loading && !error && activeTab === 'overview' && overviewData && (
            <div className="space-y-6 animate-in fade-in duration-300">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs relative overflow-hidden group">
                  <div className="p-3 bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 rounded-xl w-fit">
                    <Stethoscope className="w-6 h-6 group-hover:scale-110 transition-transform" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mt-4">Total de Médicos</h3>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-3xl font-black">{overviewData.doctors.total}</span>
                    <span className={`text-xs font-bold flex items-center ${overviewData.doctors.growthRate >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                      {overviewData.doctors.growthRate >= 0 ? '+' : ''}{overviewData.doctors.growthRate}%
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2 font-medium">Aprovados: {overviewData.doctors.approved} | Pendentes: {overviewData.doctors.pending}</p>
                </div>

                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs relative overflow-hidden group">
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl w-fit">
                    <Users className="w-6 h-6 group-hover:scale-110 transition-transform" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mt-4">Total de Pacientes</h3>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-3xl font-black">{overviewData.patients.total}</span>
                    <span className={`text-xs font-bold flex items-center ${overviewData.patients.growthRate >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                      {overviewData.patients.growthRate >= 0 ? '+' : ''}{overviewData.patients.growthRate}%
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2 font-medium">Ativos: {overviewData.patients.active} | Inativos: {overviewData.patients.inactive}</p>
                </div>

                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs relative overflow-hidden group">
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-xl w-fit">
                    <Award className="w-6 h-6 group-hover:scale-110 transition-transform" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mt-4">Novos Médicos (Período)</h3>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-3xl font-black">+{overviewData.doctors.newInPeriod}</span>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2 font-medium">Adesões profissionais no período selecionado</p>
                </div>

                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs relative overflow-hidden group">
                  <div className="p-3 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 rounded-xl w-fit">
                    <Users className="w-6 h-6 group-hover:scale-110 transition-transform" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mt-4">Novos Pacientes (Período)</h3>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-3xl font-black">+{overviewData.patients.newInPeriod}</span>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2 font-medium">Novos cadastros de pacientes no período</p>
                </div>
              </div>

              {/* Growth Visualizations */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Doctor vs Patient comparison */}
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs">
                  <h3 className="text-base font-bold mb-4">Composição de Crescimento (Cadastros)</h3>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[
                          { name: 'Médicos', Total: overviewData.doctors.total, Novos: overviewData.doctors.newInPeriod },
                          { name: 'Pacientes', Total: overviewData.patients.total, Novos: overviewData.patients.newInPeriod },
                        ]}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-neutral-800" />
                        <XAxis dataKey="name" stroke="currentColor" className="text-xs" />
                        <YAxis stroke="currentColor" className="text-xs" />
                        <Tooltip contentStyle={{ background: '#171717', color: '#fff', borderRadius: '10px' }} />
                        <Legend />
                        <Bar dataKey="Novos" fill="#0d9488" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Total" fill="#2563eb" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Conversion analysis */}
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs flex flex-col justify-between">
                  <div>
                    <h3 className="text-base font-bold mb-2">Funil de Conversão e Crescimento</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-6">Métricas de engajamento do crescimento global da plataforma</p>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <div className="flex justify-between text-xs font-semibold mb-1">
                        <span>Taxa de Crescimento de Médicos</span>
                        <span className="text-teal-600 dark:text-teal-400 font-bold">{overviewData.doctors.growthRate}%</span>
                      </div>
                      <div className="w-full bg-gray-100 dark:bg-neutral-800 rounded-full h-2">
                        <div className="bg-teal-600 h-2 rounded-full" style={{ width: `${Math.min(100, overviewData.doctors.growthRate)}%` }} />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-xs font-semibold mb-1">
                        <span>Taxa de Crescimento de Pacientes</span>
                        <span className="text-blue-600 dark:text-blue-400 font-bold">{overviewData.patients.growthRate}%</span>
                      </div>
                      <div className="w-full bg-gray-100 dark:bg-neutral-800 rounded-full h-2">
                        <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${Math.min(100, overviewData.patients.growthRate)}%` }} />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-xs font-semibold mb-1">
                        <span>Médicos Aprovados / Cadastrados</span>
                        <span className="text-amber-500 font-bold">
                          {((overviewData.doctors.approved / (overviewData.doctors.total || 1)) * 100).toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 dark:bg-neutral-800 rounded-full h-2">
                        <div className="bg-amber-500 h-2 rounded-full" style={{ width: `${(overviewData.doctors.approved / (overviewData.doctors.total || 1)) * 100}%` }} />
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-teal-50/50 dark:bg-teal-950/20 rounded-xl flex gap-3 text-xs text-teal-800 dark:text-teal-300">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    <p>
                      O crescimento contínuo de médicos aprovados e pacientes ativos garante a liquidez do ecossistema de saúde digital, reduzindo o tempo de espera no plantão.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ───────────────────────────────────────────────────────────── */}
          {/* TAB: APPOINTMENTS AND CONSULTATIONS */}
          {/* ───────────────────────────────────────────────────────────── */}
          {!loading && !error && activeTab === 'appointments' && appointmentsData && (
            <div className="space-y-6 animate-in fade-in duration-300">
              {/* Cards Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="bg-white dark:bg-neutral-900 rounded-xl p-5 border border-gray-200 dark:border-neutral-800 text-center">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Consultas Totais</span>
                  <p className="text-2xl font-black mt-1">{appointmentsData.total}</p>
                </div>
                <div className="bg-white dark:bg-neutral-900 rounded-xl p-5 border border-gray-200 dark:border-neutral-800 text-center">
                  <span className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase">Concluídas</span>
                  <p className="text-2xl font-black mt-1 text-green-600 dark:text-green-400">{appointmentsData.byStatus.completed}</p>
                </div>
                <div className="bg-white dark:bg-neutral-900 rounded-xl p-5 border border-gray-200 dark:border-neutral-800 text-center">
                  <span className="text-xs font-semibold text-blue-500 uppercase">Agendadas</span>
                  <p className="text-2xl font-black mt-1 text-blue-500">{appointmentsData.byStatus.scheduled}</p>
                </div>
                <div className="bg-white dark:bg-neutral-900 rounded-xl p-5 border border-gray-200 dark:border-neutral-800 text-center">
                  <span className="text-xs font-semibold text-amber-500 uppercase">Pendentes</span>
                  <p className="text-2xl font-black mt-1 text-amber-500">{appointmentsData.byStatus.requested}</p>
                </div>
                <div className="bg-white dark:bg-neutral-900 rounded-xl p-5 border border-gray-200 dark:border-neutral-800 text-center">
                  <span className="text-xs font-semibold text-rose-500 uppercase">Canceladas</span>
                  <p className="text-2xl font-black mt-1 text-rose-500">{appointmentsData.byStatus.cancelled}</p>
                </div>
              </div>

              {/* Visualizations row 1 */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Distribution by type */}
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs flex flex-col justify-between">
                  <h3 className="text-base font-bold mb-4">Atendimentos por Canal</h3>
                  
                  {appointmentsData.total === 0 ? (
                    <div className="h-[200px] flex flex-col items-center justify-center text-center text-gray-400">
                      <Calendar className="w-8 h-8 mb-2 text-gray-300" />
                      Sem dados de atendimento
                    </div>
                  ) : (
                    <>
                      <div className="h-[200px] flex justify-center items-center">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Telemedicina', value: appointmentsData.byType.telemedicine },
                                { name: 'Domiciliar', value: appointmentsData.byType.home },
                                { name: 'Consultório', value: appointmentsData.byType.clinic },
                              ].filter(item => item.value > 0)}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={80}
                              paddingAngle={5}
                              dataKey="value"
                            >
                              {COLORS.pie.slice(0, 3).map((color, index) => (
                                <Cell key={`cell-${index}`} fill={color} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="grid grid-cols-3 gap-2 mt-4 text-center text-xs font-semibold">
                        <div>
                          <div className="w-3 h-3 bg-[#0d9488] rounded-full mx-auto mb-1" />
                          <span>Vídeo ({appointmentsData.byType.telemedicine})</span>
                        </div>
                        <div>
                          <div className="w-3 h-3 bg-[#2563eb] rounded-full mx-auto mb-1" />
                          <span>Domicílio ({appointmentsData.byType.home})</span>
                        </div>
                        <div>
                          <div className="w-3 h-3 bg-[#fbbf24] rounded-full mx-auto mb-1" />
                          <span>Consultório ({appointmentsData.byType.clinic})</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Specialties */}
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs lg:col-span-2">
                  <h3 className="text-base font-bold mb-4">Volume por Especialidade Médica</h3>
                  
                  {appointmentsData.bySpecialty.length === 0 ? (
                    <div className="h-[250px] flex flex-col items-center justify-center text-center text-gray-400">
                      <Stethoscope className="w-8 h-8 mb-2 text-gray-300" />
                      Sem dados de especialidades
                    </div>
                  ) : (
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={appointmentsData.bySpecialty} layout="vertical" margin={{ left: 50 }}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-neutral-800" />
                          <XAxis type="number" stroke="currentColor" className="text-[10px]" />
                          <YAxis dataKey="specialty" type="category" stroke="currentColor" className="text-[10px]" />
                          <Tooltip contentStyle={{ background: '#171717', color: '#fff', borderRadius: '10px' }} />
                          <Bar dataKey="count" fill="#2563eb" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>

              {/* Visualizations row 2 (Peak hours) */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs">
                  <h3 className="text-base font-bold mb-4">Pico de Atendimentos por Hora do Dia</h3>
                  {appointmentsData.peaks.hoursOfDay.length === 0 ? (
                    <div className="h-[200px] flex items-center justify-center text-gray-400">
                      Sem dados temporais suficientes
                    </div>
                  ) : (
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={appointmentsData.peaks.hoursOfDay}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-neutral-800" />
                          <XAxis dataKey="hour" stroke="currentColor" className="text-[10px]" tickFormatter={(h) => `${h}h`} />
                          <YAxis stroke="currentColor" className="text-[10px]" />
                          <Tooltip contentStyle={{ background: '#171717', color: '#fff', borderRadius: '10px' }} />
                          <Area type="monotone" dataKey="count" name="Consultas" stroke="#0d9488" fill="#0d9488" fillOpacity={0.1} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs">
                  <h3 className="text-base font-bold mb-4">Volume por Dia da Semana</h3>
                  {appointmentsData.peaks.daysOfWeek.length === 0 ? (
                    <div className="h-[250px] flex items-center justify-center text-gray-400">
                      Sem dados semanais
                    </div>
                  ) : (
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={appointmentsData.peaks.daysOfWeek.map(d => ({
                            ...d,
                            dayName: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][d.day - 1] || `Dia ${d.day}`,
                          }))}
                        >
                          <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-neutral-800" />
                          <XAxis dataKey="dayName" stroke="currentColor" className="text-[10px]" />
                          <YAxis stroke="currentColor" className="text-[10px]" />
                          <Tooltip contentStyle={{ background: '#171717', color: '#fff', borderRadius: '10px' }} />
                          <Bar dataKey="count" name="Consultas" fill="#be123c" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ───────────────────────────────────────────────────────────── */}
          {/* TAB: FINANCIAL METRICS */}
          {/* ───────────────────────────────────────────────────────────── */}
          {!loading && !error && activeTab === 'financial' && financialData && (
            <div className="space-y-6 animate-in fade-in duration-300">
              
              {/* Warnings Alert regarding mock estimation */}
              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/50 rounded-2xl p-5 flex gap-4">
                <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-xs">
                  <h4 className="font-bold text-amber-800 dark:text-amber-300 mb-1">Informativo sobre Dados Financeiros</h4>
                  <ul className="list-disc pl-4 space-y-1 text-amber-700 dark:text-amber-400 font-medium">
                    {financialData.warnings.map((w, idx) => <li key={idx}>{w}</li>)}
                  </ul>
                </div>
              </div>

              {/* Financial cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs">
                  <span className="text-xs font-semibold text-gray-500">Faturamento Estimado (Concluído)</span>
                  <p className="text-3xl font-black mt-2 text-teal-600 dark:text-teal-400">
                    R$ {financialData.revenue.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-2 font-medium">Com base em {financialData.paidCount} consultas finalizadas</p>
                </div>

                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs">
                  <span className="text-xs font-semibold text-gray-500">Faturamento Pendente (Agendados)</span>
                  <p className="text-3xl font-black mt-2 text-blue-500">
                    R$ {financialData.revenue.pending.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-2 font-medium">Ganhos em potencial de consultas agendadas</p>
                </div>

                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs">
                  <span className="text-xs font-semibold text-gray-500">Ticket Médio Estimado</span>
                  <p className="text-3xl font-black mt-2 text-amber-500">
                    R$ {financialData.ticketAverage.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-2 font-medium">Média por atendimento executado</p>
                </div>
              </div>

              {/* Financial charts */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Evolution chart */}
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs lg:col-span-2">
                  <h3 className="text-base font-bold mb-4">Evolução do Faturamento Estimado</h3>
                  {financialData.evolution.length === 0 ? (
                    <div className="h-[250px] flex items-center justify-center text-gray-400">
                      Sem evolução faturada no período
                    </div>
                  ) : (
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={financialData.evolution}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-neutral-800" />
                          <XAxis dataKey="date" stroke="currentColor" className="text-[10px]" />
                          <YAxis stroke="currentColor" className="text-[10px]" tickFormatter={(val) => `R$${val}`} />
                          <Tooltip contentStyle={{ background: '#171717', color: '#fff', borderRadius: '10px' }} />
                          <Line type="monotone" dataKey="amount" name="Faturamento (R$)" stroke="#0d9488" strokeWidth={2} activeDot={{ r: 8 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                {/* Revenue by Type */}
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs flex flex-col justify-between">
                  <h3 className="text-base font-bold mb-4">Receita por Canal de Atendimento</h3>
                  {financialData.revenue.total === 0 ? (
                    <div className="h-[200px] flex items-center justify-center text-gray-400">
                      Sem receita faturada
                    </div>
                  ) : (
                    <>
                      <div className="h-[200px] flex justify-center items-center">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Telemedicina', value: financialData.byType.telemedicine },
                                { name: 'Domiciliar', value: financialData.byType.home },
                                { name: 'Consultório', value: financialData.byType.clinic },
                              ].filter(item => item.value > 0)}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={80}
                              paddingAngle={5}
                              dataKey="value"
                            >
                              {COLORS.pie.slice(0, 3).map((color, index) => (
                                <Cell key={`cell-${index}`} fill={color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(val: any) => typeof val === 'number' ? `R$ ${val.toFixed(2)}` : val} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="space-y-1.5 mt-4 text-xs font-semibold">
                        <div className="flex justify-between items-center">
                          <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-[#0d9488] rounded-full" /> Telemedicina</span>
                          <span>R$ {financialData.byType.telemedicine.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-[#2563eb] rounded-full" /> Domiciliar</span>
                          <span>R$ {financialData.byType.home.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-[#fbbf24] rounded-full" /> Consultório</span>
                          <span>R$ {financialData.byType.clinic.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ───────────────────────────────────────────────────────────── */}
          {/* TAB: DOCTOR PERFORMANCE AND QUALITY */}
          {/* ───────────────────────────────────────────────────────────── */}
          {!loading && !error && activeTab === 'doctors' && doctorsData && (
            <div className="space-y-6 animate-in fade-in duration-300">
              
              {/* Alerta de Atenção Administrativa */}
              {doctorsData.attentionNeeded.length > 0 && (
                <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/50 rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-3 text-red-800 dark:text-red-300 font-bold text-sm">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    Médicos Requerendo Atenção Administrativa ({doctorsData.attentionNeeded.length})
                  </div>
                  <p className="text-xs text-red-700 dark:text-red-400 mb-4 font-medium">
                    Médicos com baixa média de avaliação (&lt; 3.5), denúncias recebidas ou taxa de cancelamento elevada (&gt; 20%).
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {doctorsData.attentionNeeded.map((doc) => (
                      <div key={doc.id} className="bg-white dark:bg-neutral-900 p-4 rounded-xl border border-red-100 dark:border-red-900/50 text-xs shadow-xs space-y-2">
                        <div className="flex justify-between font-bold">
                          <span className="text-gray-900 dark:text-white">{doc.name}</span>
                          <span className="text-red-600 dark:text-red-400">{doc.specialty}</span>
                        </div>
                        <div className="h-px bg-gray-100 dark:bg-neutral-800" />
                        <div className="space-y-1">
                          <div className="flex justify-between text-gray-500">
                            <span>Avaliação Média:</span>
                            <span className="font-semibold text-gray-700 dark:text-gray-300">{doc.averageRating !== null ? `${doc.averageRating}/5` : 'N/A'}</span>
                          </div>
                          <div className="flex justify-between text-gray-500">
                            <span>Denúncias:</span>
                            <span className="font-semibold text-red-600">{doc.complaintsCount}</span>
                          </div>
                          <div className="flex justify-between text-gray-500">
                            <span>Taxa Cancelamento:</span>
                            <span className="font-semibold text-gray-700 dark:text-gray-300">{doc.cancellationRate}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* General Performance Table */}
              <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-xs overflow-hidden">
                <div className="p-6 border-b border-gray-200 dark:border-neutral-800">
                  <h3 className="text-base font-bold">Indicadores de Qualidade por Profissional</h3>
                  <p className="text-xs text-gray-500 mt-1">Dados reais consolidados do prontuário e avaliações</p>
                </div>

                {doctorsData.rankingByVolume.length === 0 ? (
                  <div className="p-12 text-center text-gray-400">Sem médicos cadastrados para exibição.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-900 text-gray-500 font-bold">
                          <th className="px-6 py-4">Médico</th>
                          <th className="px-6 py-4">Especialidade</th>
                          <th className="px-6 py-4 text-center">Consultas</th>
                          <th className="px-6 py-4 text-center">Concluídas</th>
                          <th className="px-6 py-4 text-center">Canceladas (%)</th>
                          <th className="px-6 py-4 text-center">Avaliação Média</th>
                          <th className="px-6 py-4 text-center">Denúncias</th>
                          <th className="px-6 py-4 text-center">Atenção</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-neutral-800">
                        {doctorsData.rankingByVolume.map((doc) => (
                          <tr key={doc.id} className="hover:bg-gray-50 dark:hover:bg-neutral-800/40 transition-colors">
                            <td className="px-6 py-4 font-bold text-gray-900 dark:text-white">{doc.name}</td>
                            <td className="px-6 py-4 text-gray-600 dark:text-gray-400">{doc.specialty}</td>
                            <td className="px-6 py-4 text-center font-semibold">{doc.totalAppointments}</td>
                            <td className="px-6 py-4 text-center text-green-600 dark:text-green-400 font-semibold">{doc.completedAppointments}</td>
                            <td className="px-6 py-4 text-center text-gray-600 dark:text-gray-400 font-semibold">{doc.cancellationRate}%</td>
                            <td className="px-6 py-4 text-center">
                              {doc.averageRating !== null ? (
                                <span className="inline-flex items-center gap-1 bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400 font-bold px-2 py-0.5 rounded-full">
                                  ★ {doc.averageRating}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-center font-semibold text-red-600">{doc.complaintsCount}</td>
                            <td className="px-6 py-4 text-center">
                              <span className={`inline-block w-2.5 h-2.5 rounded-full ${doc.requiresAttention ? 'bg-red-500' : 'bg-green-500'}`} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ───────────────────────────────────────────────────────────── */}
          {/* TAB: COMPLAINTS AND OCCURRENCES */}
          {/* ───────────────────────────────────────────────────────────── */}
          {!loading && !error && activeTab === 'reports' && reportsData && (
            <div className="space-y-6 animate-in fade-in duration-300">
              
              {/* Counts Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs text-center">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Denúncias Totais</span>
                  <p className="text-3xl font-black mt-1 text-gray-900 dark:text-white">{reportsData.total}</p>
                </div>
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs text-center">
                  <span className="text-xs font-semibold text-amber-500 uppercase">Aguardando Análise</span>
                  <p className="text-3xl font-black mt-1 text-amber-500">{reportsData.byStatus.pending}</p>
                </div>
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs text-center">
                  <span className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase">Confirmadas / Suspensas</span>
                  <p className="text-3xl font-black mt-1 text-red-600 dark:text-red-400">{reportsData.byStatus.approved}</p>
                </div>
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs text-center">
                  <span className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase">Tempo Médio Resolução</span>
                  <p className="text-3xl font-black mt-1 text-green-600 dark:text-green-400">{reportsData.averageResolutionTimeHours}h</p>
                </div>
              </div>

              {/* Complaints Target Comparison */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                {/* Evolution chart */}
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs">
                  <h3 className="text-base font-bold mb-4">Volume de Ocorrências no Tempo</h3>
                  {reportsData.evolution.length === 0 ? (
                    <div className="h-[250px] flex items-center justify-center text-gray-400">
                      Sem ocorrências registradas no período
                    </div>
                  ) : (
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={reportsData.evolution}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-neutral-800" />
                          <XAxis dataKey="date" stroke="currentColor" className="text-[10px]" />
                          <YAxis stroke="currentColor" className="text-[10px]" />
                          <Tooltip contentStyle={{ background: '#171717', color: '#fff', borderRadius: '10px' }} />
                          <Line type="monotone" dataKey="count" name="Denúncias" stroke="#be123c" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                {/* Target distribution */}
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs flex flex-col justify-between">
                  <h3 className="text-base font-bold mb-4">Alvos das Reclamações</h3>
                  {reportsData.total === 0 ? (
                    <div className="h-[200px] flex items-center justify-center text-gray-400">Sem dados de alvos</div>
                  ) : (
                    <>
                      <div className="h-[200px] flex justify-center items-center">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Contra Médicos', value: reportsData.against.doctors },
                                { name: 'Contra Pacientes', value: reportsData.against.patients },
                              ].filter(item => item.value > 0)}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={80}
                              paddingAngle={5}
                              dataKey="value"
                            >
                              {[COLORS.pie[3], COLORS.pie[1]].map((color, index) => (
                                <Cell key={`cell-${index}`} fill={color} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="grid grid-cols-2 gap-2 mt-4 text-center text-xs font-semibold">
                        <div>
                          <div className="w-3 h-3 bg-[#e11d48] rounded-full mx-auto mb-1" />
                          <span>Médicos ({reportsData.against.doctors})</span>
                        </div>
                        <div>
                          <div className="w-3 h-3 bg-[#2563eb] rounded-full mx-auto mb-1" />
                          <span>Pacientes ({reportsData.against.patients})</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Ranking of reported users */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs">
                  <h3 className="text-base font-bold mb-4">Médicos mais Reclamados</h3>
                  {reportsData.topDoctors.length === 0 ? (
                    <p className="text-xs text-gray-500 py-6 text-center">Nenhum médico com denúncias registradas.</p>
                  ) : (
                    <div className="space-y-3">
                      {reportsData.topDoctors.map((doc, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 rounded-lg bg-gray-50 dark:bg-neutral-800/40 text-xs font-medium">
                          <span>{doc.name}</span>
                          <span className="bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 font-bold px-2 py-0.5 rounded-full">{doc.count} queixas</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs">
                  <h3 className="text-base font-bold mb-4">Pacientes mais Reclamados</h3>
                  {reportsData.topPatients.length === 0 ? (
                    <p className="text-xs text-gray-500 py-6 text-center">Nenhum paciente com denúncias registradas.</p>
                  ) : (
                    <div className="space-y-3">
                      {reportsData.topPatients.map((pat, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 rounded-lg bg-gray-50 dark:bg-neutral-800/40 text-xs font-medium">
                          <span>{pat.name}</span>
                          <span className="bg-blue-100 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 font-bold px-2 py-0.5 rounded-full">{pat.count} queixas</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ───────────────────────────────────────────────────────────── */}
          {/* TAB: OPERATIONAL PERFORMANCE */}
          {/* ───────────────────────────────────────────────────────────── */}
          {!loading && !error && activeTab === 'operations' && operationsData && (
            <div className="space-y-6 animate-in fade-in duration-300">
              
              {/* Operations counts */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs flex gap-4 items-center">
                  <div className="p-3 bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 rounded-xl">
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 font-semibold uppercase">Aprovações no Período</span>
                    <p className="text-2xl font-black text-gray-900 dark:text-white">{operationsData.doctorApprovals} médicos</p>
                  </div>
                </div>

                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs flex gap-4 items-center">
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl">
                    <Clock className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 font-semibold uppercase">Tempo Médio Aprovação</span>
                    <p className="text-2xl font-black text-gray-900 dark:text-white">{operationsData.averageApprovalTimeDays} dias</p>
                  </div>
                </div>

                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs flex gap-4 items-center">
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-xl">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 font-semibold uppercase">Documentos Pendentes</span>
                    <p className="text-2xl font-black text-gray-900 dark:text-white">{operationsData.pendingDocuments} na fila</p>
                  </div>
                </div>
              </div>

              {/* Conversion Analysis and Audits */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                {/* Conversion indicators */}
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs flex flex-col justify-between">
                  <div>
                    <h3 className="text-base font-bold mb-2">Taxas de Conversão Cadastral</h3>
                    <p className="text-xs text-gray-500 mb-6">Eficiência do onboarding de médicos e pacientes</p>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <div className="flex justify-between text-xs font-semibold mb-1">
                        <span>Conversão Médico Cadastrado para Aprovado</span>
                        <span className="text-teal-600 dark:text-teal-400 font-bold">{operationsData.conversion.doctorRegisterToApproved}%</span>
                      </div>
                      <div className="w-full bg-gray-100 dark:bg-neutral-800 rounded-full h-2.5">
                        <div className="bg-teal-600 h-2.5 rounded-full" style={{ width: `${operationsData.conversion.doctorRegisterToApproved}%` }} />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-xs font-semibold mb-1">
                        <span>Conversão Paciente Cadastrado para Atendido</span>
                        <span className="text-blue-600 dark:text-blue-400 font-bold">{operationsData.conversion.patientRegisterToAttended}%</span>
                      </div>
                      <div className="w-full bg-gray-100 dark:bg-neutral-800 rounded-full h-2.5">
                        <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${operationsData.conversion.patientRegisterToAttended}%` }} />
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 p-4 bg-blue-50/50 dark:bg-blue-950/20 rounded-xl flex gap-3 text-xs text-blue-800 dark:text-blue-300">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    <p>
                      Mantenha a fila de revisão de documentos com baixa latência para melhorar a conversão cadastral de médicos.
                    </p>
                  </div>
                </div>

                {/* Audit & Administrative volume */}
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-200 dark:border-neutral-800 shadow-xs flex flex-col justify-between">
                  <div>
                    <h3 className="text-base font-bold mb-2">Ações Administrativas no Período</h3>
                    <p className="text-xs text-gray-500 mb-6">Volumetria de revisões e auditoria interna</p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3.5 rounded-xl bg-gray-50 dark:bg-neutral-800/40 text-xs font-semibold">
                      <span className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-teal-600" />
                        Documentos Revisados (Aprovados/Recusados)
                      </span>
                      <span className="text-gray-900 dark:text-white font-bold">{operationsData.adminActions.reviewedDocuments}</span>
                    </div>

                    <div className="flex justify-between items-center p-3.5 rounded-xl bg-gray-50 dark:bg-neutral-800/40 text-xs font-semibold">
                      <span className="flex items-center gap-2">
                        <Flag className="w-4 h-4 text-rose-600" />
                        Denúncias Processadas (Julgadas/Arquivadas)
                      </span>
                      <span className="text-gray-900 dark:text-white font-bold">{operationsData.adminActions.resolvedReports}</span>
                    </div>

                    <div className="flex justify-between items-center p-3.5 rounded-xl bg-teal-50/50 dark:bg-teal-950/10 text-xs font-bold border border-teal-100 dark:border-teal-900/50">
                      <span className="flex items-center gap-2 text-teal-800 dark:text-teal-400">
                        Total de Ações Relevantes Auditadas
                      </span>
                      <span className="text-teal-900 dark:text-teal-300 font-extrabold">{operationsData.adminActions.total}</span>
                    </div>
                  </div>

                  <div className="text-[10px] text-gray-400 font-semibold text-center mt-6 uppercase tracking-wider">
                    Log de auditoria integrado e imutável
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
